import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { CheckCircle, Lock, ChevronRight, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const phaseColors = {
  1: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30' },
  2: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
  3: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  4: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30' },
};

export default function CurriculumPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [curriculum, setCurriculum] = useState([]);
  const [loading, setLoading] = useState(true);

  const completedSet = new Set(user?.completed_modules || []);
  const currentWeek = user?.current_week || 1;

  useEffect(() => {
    const fetchCurriculum = async () => {
      try {
        const { data } = await axios.get(`${API}/curriculum`, { withCredentials: true });
        setCurriculum(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchCurriculum();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const grouped = {};
  curriculum.forEach(m => {
    if (!grouped[m.phase]) grouped[m.phase] = { name: m.phase_name, modules: [] };
    grouped[m.phase].modules.push(m);
  });

  return (
    <div data-testid="curriculum-page" className="p-6 md:p-8 lg:p-12 max-w-5xl">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30] mb-2">Curriculum</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">24-Week Program</h1>
        <p className="text-[#A3A3A3] text-sm mt-1">
          {completedSet.size} of 24 modules completed ({Math.round(completedSet.size / 24 * 100)}%)
        </p>
      </div>

      {/* Progress bar */}
      <div className="mb-8">
        <div className="w-full h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
          <div className="h-full bg-[#FF3B30] rounded-full transition-all duration-500" style={{ width: `${(completedSet.size / 24) * 100}%` }} />
        </div>
      </div>

      {Object.entries(grouped).map(([phase, { name, modules }]) => {
        const pc = phaseColors[phase] || phaseColors[1];
        return (
          <div key={phase} className="mb-8">
            <div className={`flex items-center gap-3 mb-4 ${pc.text}`}>
              <div className={`w-8 h-8 ${pc.bg} rounded-sm flex items-center justify-center border ${pc.border}`}>
                <span className="font-bold text-sm">{phase}</span>
              </div>
              <div>
                <h2 className="text-white font-semibold text-lg">{name}</h2>
                <p className="text-[#737373] text-xs">Weeks {modules[0]?.week}-{modules[modules.length - 1]?.week}</p>
              </div>
            </div>

            <div className="space-y-2">
              {modules.map((mod) => {
                const isComplete = completedSet.has(mod.week);
                const isCurrent = mod.week === currentWeek;
                const isLocked = mod.week > currentWeek && !isComplete;

                return (
                  <button
                    key={mod.week}
                    data-testid={`module-${mod.week}`}
                    onClick={() => !isLocked && navigate(`/module/${mod.week}`)}
                    disabled={isLocked}
                    className={`w-full bg-[#141414] border rounded-sm p-4 flex items-center gap-4 text-left transition-all
                      ${isComplete ? 'border-[#22C55E]/30 hover:border-[#22C55E]/50' : isCurrent ? 'border-[#FF3B30]/50 hover:border-[#FF3B30]' : isLocked ? 'border-[#262626] opacity-50 cursor-not-allowed' : 'border-[#262626] hover:border-[#FF3B30]/30'}
                    `}
                  >
                    <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0
                      ${isComplete ? 'bg-[#22C55E]/10 text-[#22C55E]' : isCurrent ? 'bg-[#FF3B30]/10 text-[#FF3B30]' : 'bg-[#1A1A1A] text-[#737373]'}`}
                    >
                      {isComplete ? <CheckCircle className="w-4 h-4" /> : isLocked ? <Lock className="w-3.5 h-3.5" /> : <span className="text-xs font-bold">{mod.week}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm ${isComplete ? 'text-[#A3A3A3]' : 'text-white'}`}>
                        Week {mod.week}: {mod.title}
                      </p>
                      <p className="text-[#737373] text-xs truncate">{mod.description}</p>
                    </div>
                    {isCurrent && (
                      <span className="text-xs font-bold bg-[#FF3B30] text-white px-2.5 py-1 rounded-sm flex items-center gap-1 shrink-0">
                        Start <ArrowRight className="w-3 h-3" />
                      </span>
                    )}
                    {isComplete && <ChevronRight className="w-4 h-4 text-[#737373] shrink-0" />}
                    {!isCurrent && !isComplete && !isLocked && <ChevronRight className="w-4 h-4 text-[#737373] shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
